import * as graphics from "@client/app/graphics";
import {AsyncButton} from "@client/components/Button";
import {CalculationModal} from "@client/components/CalculationModal";
import {SimpleIcon} from "@client/components/Icon";
import {SimpleTabs} from "@client/components/SimpleScene";
import * as dates from "@client/dates";
import {getProfile} from "@client/features/orm";
import {copyTrackedCalculatorLink} from "@client/features/sharing";
import {useQueryString} from "@client/hooks";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import {useSiteContext} from "@client/site/context";
import {useAppDispatch, useAppSelector} from "@client/store";
import * as utils from "@client/utils";
import {Listbox, ListboxButton, ListboxOption, ListboxOptions} from "@headlessui/react";
import {CheckIcon, ChevronDownIcon} from "@heroicons/react/16/solid";
import {SparklesIcon} from "@heroicons/react/24/outline";
import {classNames, reverse, server} from "@reactivated";
import * as React from "react";
import {createPortal} from "react-dom";

import peptideData from "../../server/peptides.json";

function getDaysSinceMixed(mixedAt: string): number {
    const mixedDate = new Date(mixedAt);
    const now = new Date();
    const diffMs = now.getTime() - mixedDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
}

// Build peptide options for the dropdown
type SimplePeptideKey = keyof typeof peptideData.peptides;
type BlendKey = keyof typeof peptideData.blends;
type AnyPeptideKey = SimplePeptideKey | BlendKey;

type SimplePeptide = (typeof peptideData.peptides)[SimplePeptideKey];
type BlendPeptide = (typeof peptideData.blends)[BlendKey];

type SimplePeptideOption = {
    type: "simple";
    key: SimplePeptideKey;
    peptide: SimplePeptide;
    category: string;
};

type BlendOption = {
    type: "blend";
    key: BlendKey;
    peptide: BlendPeptide;
    category: string;
};

type PeptideOption = SimplePeptideOption | BlendOption;

type ComponentDef = {
    peptide: string;
    mg: number;
};

// All blend components reference simple peptides — centralizes the single unsafe cast.
function getSimplePeptideConfig(key: string) {
    return peptideData.peptides[key as SimplePeptideKey];
}

type BlendVialPreset = {components: Record<string, number>};

function getBlendPresetTotal(preset: BlendVialPreset): number {
    return Object.values(preset.components).reduce((sum, v) => sum + v, 0);
}

function getBlendPresetLabel(preset: BlendVialPreset): string {
    const total = getBlendPresetTotal(preset);
    const parts = Object.values(preset.components).join("/");
    return `${total} mg (${parts})`;
}

function findMatchingPreset(
    presets: BlendVialPreset[],
    componentAmounts: Record<string, number | null>,
): BlendVialPreset | null {
    return (
        presets.find((preset) => {
            const keys = Object.keys(preset.components);
            return keys.every((k) => componentAmounts[k] === preset.components[k]);
        }) ?? null
    );
}

type DosePreset = {value: number; unit: Unit};

type PeptideWithDoses = SimplePeptide & {
    doses: DosePreset[];
};

// Smart mix constraints (defined early for use in getMaxConcentration)
const DEFAULT_MAX_CONCENTRATION = 20; // mg/mL

function getMaxConcentration(
    peptide: SimplePeptide | BlendPeptide,
    componentAmounts?: Record<string, number | null>,
): number {
    if ("components" in peptide) {
        const components = peptide.components;

        const getComponentMg = (comp: ComponentDef) =>
            componentAmounts?.[comp.peptide] ?? comp.mg;

        const totalMg = components.reduce((sum, c) => sum + getComponentMg(c), 0);
        if (totalMg <= 0) return DEFAULT_MAX_CONCENTRATION;

        const effectiveMaxes = components.map((comp) => {
            const compPeptide = getSimplePeptideConfig(comp.peptide);
            const compMax =
                "maxConcentration" in compPeptide
                    ? compPeptide.maxConcentration
                    : DEFAULT_MAX_CONCENTRATION;
            const compMg = getComponentMg(comp);
            if (compMg <= 0) return Infinity;
            return compMax * (totalMg / compMg);
        });

        return Math.min(...effectiveMaxes);
    }

    return "maxConcentration" in peptide
        ? peptide.maxConcentration
        : DEFAULT_MAX_CONCENTRATION;
}

function getAnchorPeptide(peptide: {components: ComponentDef[]}) {
    const anchorKey = peptide.components[0].peptide;
    return {
        key: anchorKey,
        config: getSimplePeptideConfig(anchorKey) as PeptideWithDoses,
        componentMg: peptide.components[0].mg,
    };
}

function getDoseOptions(peptide: SimplePeptide) {
    if ("doses" in peptide) {
        return (peptide as PeptideWithDoses).doses.map((d) => ({
            value: d.value,
            label: `${d.value} ${d.unit}`,
        }));
    }
    return [];
}

const peptideOptions: PeptideOption[] = peptideData.categories.flatMap((category) =>
    category.peptides.map((key): PeptideOption => {
        if (key in peptideData.blends) {
            const blendKey = key as BlendKey;
            return {
                type: "blend",
                key: blendKey,
                peptide: peptideData.blends[blendKey],
                category: category.name,
            };
        }
        const simpleKey = key as SimplePeptideKey;
        return {
            type: "simple",
            key: simpleKey,
            peptide: peptideData.peptides[simpleKey],
            category: category.name,
        };
    }),
);

const SYRINGE_CONFIG: Record<
    number,
    {totalUnits: number; majorEvery: number; stepEvery: number}
> = {
    0.3: {totalUnits: 30, majorEvery: 5, stepEvery: 1},
    0.5: {totalUnits: 50, majorEvery: 5, stepEvery: 1},
    1: {totalUnits: 100, majorEvery: 10, stepEvery: 2},
};

// Smart mix constraints (continued)
const MAX_SUBQ_VOLUME_ML = 0.5;
const ABSOLUTE_MAX_CONCENTRATION = 100; // mg/mL - sanity check
const MIN_WATER_ML = 0.3; // lowered from 0.5
const MAX_WATER_ML = 3.0;

const HOW_WE_CALCULATE_CONTENT = `
## Calculation rules

We recommend a water amount and draw volume that balances safety with convenience.

### Safety Rules

- **Maximum injection volume**: Each dose should be 0.5 mL or less for subcutaneous injection
- **Concentration limits**: We ensure the peptide isn't too concentrated for practical use. Most peptides work best with moderate concentrations; a few (like Tirzepatide and GHK-Cu) handle higher concentrations
- **Minimum water**: At least 0.3 mL of water per vial to keep the mix workable

If no combination meets these rules, we'll suggest trying manual mode.

### Preferences

When several water amounts satisfy safety rules, we pick the best one based on:

- Lands on a major tick mark on your syringe (easier to draw accurately)
- Uses a round water amount (like 1 mL or 2 mL)
- Creates easy mental math (e.g., 10 units = 1 mg of peptide)
- Smaller draw volumes when possible

### What We Don't Consider

This calculator focuses on physical feasibility only. We don't factor in sting, burning, or injection comfort—some mixes may be unpleasant even when technically workable.

### Important

This is a calculation tool, not medical advice. Consult a healthcare professional for dosing guidance.
`.trim();

type SmartMixOption = {
    units: number;
    water: number;
    isTenMark: boolean;
    isNiceWater: boolean;
    score: number;
};

// Check if a number is "round" (no messy decimals)
function isRoundNumber(n: number): boolean {
    // Round numbers: integers, or clean halves/quarters
    const rounded = Math.round(n * 4) / 4;
    return Math.abs(n - rounded) < 0.001;
}

function calculateSmartMix(
    vialMg: number,
    doseMcg: number,
    syringeConfig: {totalUnits: number; majorEvery: number; stepEvery: number},
    maxConcentration: number = DEFAULT_MAX_CONCENTRATION,
): SmartMixOption[] {
    const {totalUnits, majorEvery, stepEvery} = syringeConfig;
    const options: SmartMixOption[] = [];

    // Generate target units based on syringe tick marks (stepEvery)
    // This ensures we only suggest units that land on actual marks
    for (
        let targetUnit = stepEvery;
        targetUnit <= totalUnits;
        targetUnit += stepEvery
    ) {
        // Formula: waterNeeded = (targetUnit * 10 * vialMg) / doseMcg
        // This comes from: doseMcg / (vialMg * 1000 / waterMl) = targetUnit / 100
        // Solving for waterMl: waterMl = (targetUnit * 10 * vialMg) / doseMcg
        const waterNeeded = (targetUnit * 10 * vialMg) / doseMcg;

        // Constraint checks
        // 1. Water amount bounds
        if (waterNeeded < MIN_WATER_ML || waterNeeded > MAX_WATER_ML) {
            continue;
        }

        // 2. Concentration limit: vialMg / waterMl must be <= maxConcentration
        const concentration = vialMg / waterNeeded;
        if (
            concentration > maxConcentration ||
            concentration > ABSOLUTE_MAX_CONCENTRATION
        ) {
            continue;
        }

        // 3. Dose volume limit: doseMcg / (concentration * 1000) must be <= MAX_SUBQ_VOLUME_ML
        const doseVolumeMl = doseMcg / (concentration * 1000);
        if (doseVolumeMl > MAX_SUBQ_VOLUME_ML) {
            continue;
        }

        const isMajorMark = targetUnit % majorEvery === 0;
        const isNiceWater = waterNeeded === Math.round(waterNeeded * 2) / 2;

        // Standard dilution = 10 units per mg (easiest mental math)
        // Happens when waterMl * 10 === vialMg (e.g., 10mg + 1ml, 20mg + 2ml)
        const isStandardDilution = Math.abs(waterNeeded * 10 - vialMg) < 0.001;

        // Round concentration bonus: prefer water amounts where the math is easy
        // unitsPerMg = how many units to draw for 1mg of peptide
        // e.g., 10mg vial + 1ml water = 10 units/mg (easy math!)
        // e.g., 30mg vial + 3ml water = 10 units/mg (same ratio)
        const unitsPerMg = (waterNeeded * 100) / vialMg;
        const unitsPerMgAlignsMajor = unitsPerMg % majorEvery === 0;
        const unitsPerMgAlignsStep = unitsPerMg % stepEvery === 0;
        const isRoundConcentration = isRoundNumber(unitsPerMg) && unitsPerMgAlignsStep;
        const roundConcentrationBonus =
            (unitsPerMgAlignsMajor ? 75 : 0) + (isRoundConcentration ? 50 : 0);

        // Scoring: prefer major marks, round concentrations, smaller draws, nice water amounts
        const smallDrawBonus = isMajorMark ? 50 / (targetUnit / majorEvery) : 0;

        options.push({
            units: targetUnit,
            water: Math.round(waterNeeded * 100) / 100,
            isTenMark: isMajorMark,
            isNiceWater,
            score:
                (isMajorMark ? 100 : 0) +
                roundConcentrationBonus +
                smallDrawBonus +
                (isStandardDilution ? 75 : 0) +
                (isNiceWater ? 50 : 0) +
                (50 - Math.abs(waterNeeded - 2)),
        });
    }

    return options.sort((a, b) => b.score - a.score).slice(0, 5);
}

type ResultMode = "auto" | "manual";

const MAX_MANUAL_WATER_ML = 10;

// Parse manual water input
const parseWaterInput = (
    input: string,
): {value: number | null; error: string | null} => {
    if (input.trim() === "") {
        return {value: null, error: "Enter a water amount"};
    }
    const num = parseFloat(input);
    if (isNaN(num)) {
        return {value: null, error: "Enter valid amount"};
    }
    if (num <= 0) {
        return {value: null, error: "Amount must be positive"};
    }
    if (num > MAX_MANUAL_WATER_ML) {
        return {value: null, error: `Maximum is ${MAX_MANUAL_WATER_ML} ml`};
    }
    return {value: num, error: null};
};

// Calculate units from water amount
const calculateUnits = (vialMg: number, doseMcg: number, waterMl: number): number =>
    (doseMcg * waterMl) / (vialMg * 10);

function ModeToggle(props: {mode: ResultMode; onChange: (mode: ResultMode) => void}) {
    const {mode, onChange} = props;
    return (
        <div className="flex rounded-lg bg-white/20 p-0.5">
            <button
                type="button"
                onClick={mode === "manual" ? () => onChange("auto") : undefined}
                aria-pressed={mode === "auto"}
                className={classNames(
                    "rounded-md px-3 py-1 text-sm font-medium",
                    mode === "auto"
                        ? "bg-white/30 text-white"
                        : "text-white/70 hover:text-white",
                )}
            >
                Auto
            </button>
            <button
                type="button"
                onClick={mode === "auto" ? () => onChange("manual") : undefined}
                aria-pressed={mode === "manual"}
                className={classNames(
                    "rounded-md px-3 py-1 text-sm font-medium",
                    mode === "manual"
                        ? "bg-white/30 text-white"
                        : "text-white/70 hover:text-white",
                )}
            >
                Manual
            </button>
        </div>
    );
}

function SmartMixResult(props: {
    vialMg: number | null;
    doseMcg: number | null;
    syringeCapacity: number | null;
    maxConcentration: number;
    disabled?: boolean;
    onSaveVial?: () => Promise<void>;
    onShare: () => Promise<void>;
    resultMode: ResultMode;
    onResultModeChange: (mode: ResultMode) => void;
    manualWaterInput: string;
    onManualWaterInputChange: (value: string) => void;
    // Calculated values passed from parent
    units: number | null;
    validationError: string | null;
    onShowHowWeCalculate: () => void;
}) {
    const {
        vialMg,
        doseMcg,
        syringeCapacity,
        maxConcentration,
        disabled = false,
        onSaveVial,
        onShare,
        resultMode,
        onResultModeChange,
        manualWaterInput,
        onManualWaterInputChange,
        units,
        validationError,
        onShowHowWeCalculate,
    } = props;

    const config =
        syringeCapacity != null
            ? (SYRINGE_CONFIG[syringeCapacity] ?? SYRINGE_CONFIG[1])
            : SYRINGE_CONFIG[1];

    const smartOptions = React.useMemo(() => {
        if (
            vialMg == null ||
            doseMcg == null ||
            vialMg <= 0 ||
            doseMcg <= 0 ||
            syringeCapacity == null
        ) {
            return [];
        }
        return calculateSmartMix(vialMg, doseMcg, config, maxConcentration);
    }, [vialMg, doseMcg, config, maxConcentration, syringeCapacity]);

    const isOverflow = units != null && units > config.totalUnits;

    const cardHeight = "h-38"; // Fixed height for consistent card size across all states

    // Disabled state: dashed border with icon
    if (disabled || vialMg == null || doseMcg == null || syringeCapacity == null) {
        return (
            <div
                className={classNames(
                    "flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-indigo-200 p-4 dark:border-slate-600",
                    cardHeight,
                )}
            >
                <SparklesIcon className="mb-2 size-8 text-indigo-300 dark:text-slate-500" />
                <div className="text-center text-indigo-300 dark:text-slate-500">
                    Select a vial size and dose
                </div>
            </div>
        );
    }

    const howWeCalculateLink = (
        <button
            type="button"
            onClick={onShowHowWeCalculate}
            className="text-sm text-indigo-200 underline hover:no-underline"
        >
            How we calculate
        </button>
    );

    const shareButton = (
        <AsyncButton
            onClick={onShare}
            aria-label="Share"
            className="rounded-lg bg-white/20 p-1.5 px-2 hover:bg-white/30"
        >
            <SimpleIcon
                icon="custom-apple-share"
                className="mb-0.5 size-4 text-white"
            />
        </AsyncButton>
    );

    // Auto mode with no results: show message with "Try manual" link
    if (resultMode === "auto" && smartOptions.length === 0) {
        return (
            <div
                className={classNames(
                    "flex flex-col rounded-xl bg-indigo-600 p-4 text-white",
                    cardHeight,
                )}
            >
                <div className="flex flex-1 flex-col items-center justify-center text-center text-indigo-200">
                    <div>No recommendation for these options.</div>
                    <button
                        type="button"
                        onClick={() => onResultModeChange("manual")}
                        className="mt-1 underline hover:no-underline"
                    >
                        Try manual
                    </button>
                </div>
                <div className="flex justify-end">{howWeCalculateLink}</div>
            </div>
        );
    }

    // Manual mode: show fields layout with 50/50 width
    if (resultMode === "manual") {
        const manualStatusMessage = (() => {
            if (validationError != null) {
                return validationError;
            }
            if (isOverflow) {
                return "Exceeds syringe capacity";
            }
            return null;
        })();

        return (
            <div
                className={classNames(
                    "flex flex-col rounded-xl bg-indigo-600 p-4 text-white",
                    cardHeight,
                )}
            >
                {/* Content area - expands to fill space */}
                <div className="flex-1">
                    {/* Row 1: Mode toggle + Share/Save buttons */}
                    <div className="flex items-center justify-between">
                        <ModeToggle mode={resultMode} onChange={onResultModeChange} />
                        {onSaveVial != null && (
                            <div className="flex gap-2">
                                {shareButton}
                                <AsyncButton
                                    onClick={onSaveVial}
                                    className="rounded-lg bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30"
                                >
                                    Save vial
                                </AsyncButton>
                            </div>
                        )}
                    </div>

                    {/* Row 2: Water input + Draw units (50/50 width) */}
                    <div className="mt-3 flex gap-3">
                        {/* Water input */}
                        <div className="flex w-1/2 items-center rounded-lg bg-white/20 px-3 py-2 outline-1 -outline-offset-1 outline-white/50 outline-solid">
                            <span className="text-sm text-white/70">Water</span>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={manualWaterInput}
                                onChange={(e) =>
                                    onManualWaterInputChange(e.target.value)
                                }
                                placeholder="—"
                                className="min-w-0 flex-1 bg-transparent text-right text-base font-medium text-white placeholder:text-white/50 focus:outline-none"
                            />
                            <span className="ml-1 text-sm text-white/70">ml</span>
                        </div>

                        {/* Draw units */}
                        <div className="flex w-1/2 items-center rounded-lg bg-white/20 px-3 py-2">
                            <span className="text-sm text-white/70">Draw</span>
                            <span className="flex-1 text-right text-base font-medium text-white">
                                {units != null
                                    ? units % 1 === 0
                                        ? units.toString()
                                        : units.toFixed(1)
                                    : "—"}
                            </span>
                            <span className="ml-1 text-sm text-white/70">units</span>
                        </div>
                    </div>
                </div>

                {/* Bottom row: Status message + How we calculate */}
                <div className="flex items-center justify-between text-sm">
                    <span className={isOverflow ? "text-amber-200" : "text-indigo-200"}>
                        {manualStatusMessage}
                    </span>
                    {howWeCalculateLink}
                </div>
            </div>
        );
    }

    // Auto mode with results: prose style like before
    const best = smartOptions[0];

    return (
        <div
            className={classNames(
                "flex flex-col rounded-xl bg-indigo-600 p-4 text-white",
                cardHeight,
            )}
        >
            {/* Content area - expands to fill space */}
            <div className="flex-1">
                <div className="flex items-start justify-between">
                    <ModeToggle mode={resultMode} onChange={onResultModeChange} />
                    {onSaveVial != null && (
                        <div className="flex gap-2">
                            {shareButton}
                            <AsyncButton
                                onClick={onSaveVial}
                                className="rounded-lg bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30"
                            >
                                Save vial
                            </AsyncButton>
                        </div>
                    )}
                </div>
                <div className="mt-2 text-lg">
                    Add <span className="text-xl font-bold">{best.water} ml</span> water
                </div>
                <div className="text-lg">
                    Draw to <span className="text-xl font-bold">{best.units}</span>{" "}
                    units
                </div>
            </div>

            {/* Bottom row: Status message + How we calculate */}
            <div className="flex items-center justify-between text-sm">
                <span className="text-indigo-200">
                    {best.isTenMark === true && "Lands on a major tick mark"}
                </span>
                {howWeCalculateLink}
            </div>
        </div>
    );
}

const SYRINGE_CAPACITY_OPTIONS = [0.3, 0.5, 1] as const;

function SyringeIllustration(props: {
    capacity: number | null;
    fillUnits?: number;
    onCapacityChange: (capacity: number) => void;
    disabled?: boolean;
    overflow?: boolean;
}) {
    const {
        capacity,
        fillUnits = 0,
        onCapacityChange,
        disabled = false,
        overflow = false,
    } = props;

    const config =
        capacity != null
            ? (SYRINGE_CONFIG[capacity] ?? SYRINGE_CONFIG[1])
            : SYRINGE_CONFIG[1];
    const {totalUnits, majorEvery, stepEvery} = config;

    // Generate all tick positions
    const ticks: {unit: number; isMajor: boolean}[] = [];
    for (let unit = 0; unit <= totalUnits; unit += stepEvery) {
        ticks.push({unit, isMajor: unit % majorEvery === 0});
    }

    const fillPercent = Math.min(100, Math.max(0, (fillUnits / totalUnits) * 100));

    return (
        <div
            className={classNames(
                "rounded-xl bg-white py-4 px-4 pr-4.5 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 outline-solid transition-colors duration-200 dark:bg-slate-800 dark:text-slate-100 dark:outline-slate-600",
                disabled && "opacity-40 pointer-events-none",
                overflow && "opacity-60",
            )}
        >
            <div className="flex items-center justify-between">
                <div className="text-base font-normal">Syringe</div>
                <div className="flex gap-1.5">
                    {SYRINGE_CAPACITY_OPTIONS.map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            onClick={() => onCapacityChange(opt)}
                            className={classNames(
                                "rounded-md px-3 py-1 text-sm font-medium transition-colors",
                                capacity === opt
                                    ? "bg-indigo-600 text-white"
                                    : "bg-stone-100 text-gray-700 hover:bg-stone-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600",
                            )}
                        >
                            {opt} ml
                        </button>
                    ))}
                </div>
            </div>
            <div className="relative mt-5 mb-0 h-12">
                {/* Fill */}
                <div
                    className={classNames(
                        "absolute top-0 left-0 h-5",
                        overflow
                            ? "bg-red-400/60"
                            : "bg-indigo-300/60 dark:bg-indigo-400/60",
                    )}
                    style={{width: `${fillPercent}%`}}
                />

                {/* Tick marks */}
                <div className="absolute inset-0">
                    {ticks.map(({unit, isMajor}) => (
                        <div
                            key={unit}
                            className="absolute top-0"
                            style={{left: `${(unit / totalUnits) * 100}%`}}
                        >
                            <div
                                className={classNames(
                                    "w-px bg-black dark:bg-slate-200",
                                    isMajor ? "h-5" : "h-1.5",
                                )}
                            />
                            {isMajor && unit > 0 && (
                                <span className="absolute top-6 -translate-x-1/2 text-xs text-gray-800 dark:text-slate-300">
                                    {unit}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex items-center justify-center gap-2 text-center text-sm text-gray-800 dark:text-slate-300">
                <span className="font-normal">{capacity} ml</span> •{" "}
                <span className="font-normal">{totalUnits} units</span>
            </div>
        </div>
    );
}

const MAX_VISIBLE_OPTIONS = 4;

type Unit = "mg" | "mcg" | "IU";

function QuickSelectCard(props: {
    label: string;
    options: {value: number; label: string}[];
    value: number | null;
    onChange: (value: number | null) => void;
    otherPlaceholder: string;
    disabled?: boolean;
    unit: Unit;
    onUnitChange: (unit: Unit) => void;
    /** IU per mg ratio. If provided, IU unit option is shown. E.g., 3 means 1 mg = 3 IU */
    iuPerMg?: number;
    /** "vial" uses mg as base, "dose" uses mcg as base for IU conversion */
    mode: "vial" | "dose";
    /** When true, suppresses the text input in "Other" mode (used for compound peptides) */
    hideOtherInput?: boolean;
    /** When true, hides the unit dropdown in the "Other" input row */
    hideUnitSelector?: boolean;
    /** When true, initialize with "Other" selected (for URL-restored compound state) */
    forceOther?: boolean;
    children?: React.ReactNode;
}) {
    const {
        label,
        options,
        value,
        onChange,
        otherPlaceholder,
        disabled = false,
        unit,
        onUnitChange,
        iuPerMg,
        mode,
        hideOtherInput = false,
        hideUnitSelector = false,
        forceOther = false,
        children,
    } = props;
    const initialIsOther =
        forceOther || (value != null && !options.some((opt) => opt.value === value));
    const [isOther, setIsOther] = React.useState(initialIsOther);
    const [otherValue, setOtherValue] = React.useState(
        initialIsOther && value != null ? value.toString() : "",
    );

    const visibleOptions = options.slice(0, MAX_VISIBLE_OPTIONS);
    const hiddenOptions = options.slice(MAX_VISIBLE_OPTIONS);

    const handleQuickSelect = (val: number, isHiddenOption: boolean) => {
        // Stay expanded if selecting a hidden option, collapse if selecting a visible one
        if (!isHiddenOption) {
            setIsOther(false);
        }
        // Clear custom input when selecting a predefined option
        setOtherValue("");
        onChange(val);
    };

    const handleOtherClick = () => {
        setIsOther(true);
        onChange(null);
    };

    const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setOtherValue(val);
        const num = parseFloat(val);
        onChange(isNaN(num) ? null : num);
    };

    const handleUnitChange = (newUnit: Unit) => {
        if (newUnit === unit) return;

        // Convert the current value to the new unit
        const currentNum = parseFloat(otherValue);
        if (!isNaN(currentNum) && iuPerMg != null) {
            let converted: number;
            // mcg per IU = 1000 / iuPerMg (since 1 mg = iuPerMg IU, so 1 IU = 1000/iuPerMg mcg)
            const mcgPerIU = 1000 / iuPerMg;

            if (mode === "vial") {
                // Vial mode: base unit is mg
                // mg -> mcg: * 1000, mg -> IU: * iuPerMg
                // mcg -> mg: / 1000, mcg -> IU: / 1000 * iuPerMg
                // IU -> mg: / iuPerMg, IU -> mcg: / iuPerMg * 1000
                const valueInMg =
                    unit === "mg"
                        ? currentNum
                        : unit === "mcg"
                          ? currentNum / 1000
                          : currentNum / iuPerMg;
                converted =
                    newUnit === "mg"
                        ? valueInMg
                        : newUnit === "mcg"
                          ? valueInMg * 1000
                          : valueInMg * iuPerMg;
            } else {
                // Dose mode: base unit is mcg
                // mcg -> mg: / 1000, mcg -> IU: / mcgPerIU
                // mg -> mcg: * 1000, mg -> IU: * 1000 / mcgPerIU
                // IU -> mcg: * mcgPerIU, IU -> mg: * mcgPerIU / 1000
                const valueInMcg =
                    unit === "mcg"
                        ? currentNum
                        : unit === "mg"
                          ? currentNum * 1000
                          : currentNum * mcgPerIU;
                converted =
                    newUnit === "mcg"
                        ? valueInMcg
                        : newUnit === "mg"
                          ? valueInMcg / 1000
                          : valueInMcg / mcgPerIU;
            }

            const convertedStr =
                converted === Math.round(converted)
                    ? converted.toString()
                    : converted.toFixed(2);
            setOtherValue(convertedStr);
            onChange(converted);
        } else if (!isNaN(currentNum)) {
            // No IU support, only mg <-> mcg conversion
            const converted = newUnit === "mg" ? currentNum / 1000 : currentNum * 1000;
            const convertedStr =
                converted === Math.round(converted)
                    ? converted.toString()
                    : converted.toFixed(2);
            setOtherValue(convertedStr);
            onChange(converted);
        }

        onUnitChange(newUnit);
    };

    // +1 for the "Other" button, minimum 5 columns when disabled to maintain layout
    const columnCount = disabled ? 5 : visibleOptions.length + 1;

    return (
        <div
            className={classNames(
                "rounded-xl bg-white py-3 px-4 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 outline-solid transition-colors duration-200 dark:bg-slate-800 dark:text-slate-100 dark:outline-slate-600",
                disabled && "opacity-40 pointer-events-none",
            )}
        >
            <div className="mb-2 text-base font-normal">{label}</div>
            <div
                className="grid gap-2"
                style={{gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`}}
            >
                {disabled ? (
                    // Show placeholder buttons when disabled
                    <>
                        {[1, 2, 3, 4].map((i) => (
                            <div
                                key={i}
                                className="rounded-lg bg-stone-100 py-2 text-sm font-medium text-transparent select-none dark:bg-slate-700"
                            >
                                —
                            </div>
                        ))}
                        <div className="rounded-lg bg-stone-100 py-2 text-center text-sm font-medium text-gray-400 dark:bg-slate-700 dark:text-slate-500">
                            Other
                        </div>
                    </>
                ) : (
                    <>
                        {visibleOptions.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => handleQuickSelect(opt.value, false)}
                                className={classNames(
                                    "rounded-lg font-medium transition-colors",
                                    opt.label.startsWith("0.") ||
                                        opt.label.endsWith("mcg")
                                        ? "text-[0.75rem] pt-2.25 pb-1.75"
                                        : "text-sm py-2",
                                    !isOther && value === opt.value
                                        ? "bg-indigo-600 text-white"
                                        : "bg-stone-100 text-gray-900 hover:bg-stone-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600",
                                )}
                            >
                                {opt.label}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={handleOtherClick}
                            className={classNames(
                                "rounded-lg py-2 text-sm font-medium transition-colors",
                                isOther
                                    ? "bg-indigo-600 text-white"
                                    : "bg-stone-100 text-gray-900 hover:bg-stone-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600",
                            )}
                        >
                            Other
                        </button>
                    </>
                )}
            </div>
            {isOther && !hideOtherInput && (
                <div className="mt-3 space-y-2">
                    {hiddenOptions.length > 0 && (
                        <div
                            className="grid gap-2"
                            style={{
                                gridTemplateColumns: `repeat(${hiddenOptions.length}, minmax(0, 1fr))`,
                            }}
                        >
                            {hiddenOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => handleQuickSelect(opt.value, true)}
                                    className={classNames(
                                        "rounded-lg py-2 text-sm font-medium transition-colors",
                                        value === opt.value
                                            ? "bg-indigo-600 text-white"
                                            : "bg-stone-100 text-gray-900 hover:bg-stone-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600",
                                    )}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="flex items-center rounded-md bg-white pl-3 outline-1 -outline-offset-1 outline-gray-300 outline-solid has-[input:focus-within]:outline-2 has-[input:focus-within]:-outline-offset-2 has-[input:focus-within]:outline-indigo-600 dark:bg-slate-700 dark:outline-slate-500">
                        <input
                            type="text"
                            inputMode="decimal"
                            value={otherValue}
                            onChange={handleOtherChange}
                            placeholder={otherPlaceholder}
                            className="block min-w-0 grow py-1.5 pr-3 pl-1 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none sm:text-sm/6 dark:text-slate-100 dark:placeholder:text-slate-500"
                        />
                        {!hideUnitSelector && (
                            <div className="grid shrink-0 grid-cols-1 focus-within:relative">
                                <select
                                    value={unit}
                                    onChange={(e) =>
                                        handleUnitChange(e.target.value as Unit)
                                    }
                                    aria-label="Unit"
                                    className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-transparent py-1.5 pr-7 pl-3 text-base text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 focus:outline-solid sm:text-sm/6 dark:text-slate-400"
                                >
                                    <option value="mg">mg</option>
                                    <option value="mcg">mcg</option>
                                    {iuPerMg != null && <option value="IU">IU</option>}
                                </select>
                                <ChevronDownIcon
                                    aria-hidden="true"
                                    className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4 dark:text-slate-400"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
            {children}
        </div>
    );
}

function CompoundVialInputs(props: {
    components: ComponentDef[];
    componentAmounts: Record<string, number | null>;
    onComponentChange: (peptideKey: string, value: number | null) => void;
}) {
    const {components, componentAmounts, onComponentChange} = props;

    return (
        <div className="space-y-2">
            {components.map((comp) => {
                const compPeptide = getSimplePeptideConfig(comp.peptide);
                return (
                    <QuickSelectCard
                        key={comp.peptide}
                        label={compPeptide.name}
                        options={compPeptide.commonVials.map((v) => ({
                            value: v,
                            label: `${v} mg`,
                        }))}
                        value={componentAmounts[comp.peptide] ?? null}
                        onChange={(val) => onComponentChange(comp.peptide, val)}
                        otherPlaceholder="Enter mg"
                        unit="mg"
                        onUnitChange={() => {}}
                        mode="vial"
                        hideUnitSelector
                    />
                );
            })}
        </div>
    );
}

function formatNumber(n: number | string): string {
    const num = typeof n === "string" ? parseFloat(n) : n;
    return num % 1 === 0 ? num.toString() : num.toFixed(1);
}

function formatDoseMcg(mcg: number): string {
    if (mcg >= 1000) {
        const mg = mcg / 1000;
        return `${formatNumber(mg)} mg`;
    }
    return `${formatNumber(mcg)} mcg`;
}

function CompoundDoseBreakdown(props: {
    components: ComponentDef[];
    anchorDoseMcg: number;
    anchorMg: number;
    componentAmounts?: Record<string, number | null>;
}) {
    const {components, anchorDoseMcg, anchorMg, componentAmounts} = props;

    return (
        <div className="mt-3 border-t border-stone-200 pt-3 dark:border-slate-600">
            <div className="space-y-1">
                {components.map((comp) => {
                    const compPeptide = getSimplePeptideConfig(comp.peptide);
                    const compMg = componentAmounts?.[comp.peptide] ?? comp.mg;
                    const compDoseMcg = anchorDoseMcg * (compMg / anchorMg);
                    return (
                        <div
                            key={comp.peptide}
                            className="flex items-center justify-between text-sm"
                        >
                            <span className="text-stone-700 dark:text-slate-300">
                                {compPeptide.name}
                            </span>
                            <span className="font-medium text-stone-900 dark:text-slate-100">
                                {formatDoseMcg(compDoseMcg)}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function PeptideDropdown(props: {
    selectedOption: PeptideOption | null;
    onChange: (option: PeptideOption) => void;
}) {
    const {selectedOption, onChange} = props;
    const placeholderRef = React.useRef<HTMLSpanElement>(null);
    const [rect, setRect] = React.useState<DOMRect | null>(null);

    const showLearnMore =
        selectedOption != null &&
        selectedOption.key !== "custom" &&
        selectedOption.peptide.detail?.guide != null;

    React.useEffect(() => {
        const el = placeholderRef.current;
        if (!showLearnMore || !el) {
            setRect(null);
            return;
        }

        const sync = () => setRect(el.getBoundingClientRect());
        sync();

        window.addEventListener("scroll", sync, true);
        window.addEventListener("resize", sync);
        return () => {
            window.removeEventListener("scroll", sync, true);
            window.removeEventListener("resize", sync);
        };
    }, [showLearnMore, selectedOption]);

    return (
        <>
            <Listbox value={selectedOption} onChange={onChange}>
                <div className="relative">
                    <ListboxButton className="grid w-full cursor-default grid-cols-1 rounded-xl bg-white py-3 pr-3 pl-4 text-left text-gray-900 outline-1 -outline-offset-1 outline-gray-300 transition-colors duration-200 outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid dark:bg-slate-800 dark:text-slate-100 dark:outline-slate-600">
                        <span className="col-start-1 row-start-1 flex flex-col">
                            {selectedOption != null ? (
                                <>
                                    <span className="text-lg font-medium">
                                        {selectedOption.peptide.name}
                                    </span>
                                    <span className="text-sm">
                                        <span className="text-gray-700 dark:text-slate-400">
                                            {selectedOption.category}
                                        </span>
                                        {showLearnMore && (
                                            <span className="relative">
                                                <span className="text-gray-700 dark:text-slate-400">
                                                    {" · "}
                                                </span>
                                                {/* Visible placeholder; invisible portal hitbox overlays it to avoid triggering Listbox */}
                                                <span
                                                    ref={placeholderRef}
                                                    className="text-indigo-600 dark:text-indigo-400"
                                                >
                                                    Learn more
                                                </span>
                                            </span>
                                        )}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="text-lg font-medium">
                                        Select a peptide
                                    </span>
                                    <span className="text-sm text-gray-700 dark:text-slate-400">
                                        Tap to choose from the list
                                    </span>
                                </>
                            )}
                        </span>

                        <SimpleIcon
                            icon="0830-chevrons-expand-vertical"
                            className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 dark:text-slate-400"
                        />
                    </ListboxButton>

                    <ListboxOptions
                        anchor="bottom"
                        transition
                        className="absolute z-10 mt-1 w-(--button-width) overflow-auto rounded-xl bg-white text-base outline-1 outline-black/5 outline-solid [--anchor-max-height:60dvh] data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 dark:bg-slate-800 dark:outline-slate-600"
                    >
                        {peptideData.categories.map((category) => (
                            <div key={category.name}>
                                <div className="bg-stone-50 px-4 py-2.5 text-xs font-semibold tracking-wider text-stone-400 uppercase dark:bg-slate-900 dark:text-slate-500">
                                    {category.name}
                                </div>
                                {category.peptides.map((key) => {
                                    const option = peptideOptions.find(
                                        (opt) => opt.key === key,
                                    )!;
                                    return (
                                        <ListboxOption
                                            key={key}
                                            value={option}
                                            className="group relative cursor-default py-3 pr-9 pl-4 text-gray-900 select-none data-focus:bg-indigo-600 data-focus:text-white data-focus:outline-hidden dark:text-slate-100"
                                        >
                                            <div className="flex flex-col">
                                                <span className="block truncate font-medium group-data-selected:font-semibold">
                                                    {option.peptide.name}
                                                </span>
                                                <span className="block truncate text-sm text-gray-700 group-data-focus:text-indigo-200 dark:text-slate-400">
                                                    {option.peptide.description}
                                                </span>
                                            </div>
                                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-data-focus:text-white group-[:not([data-selected])]:hidden">
                                                <CheckIcon
                                                    aria-hidden="true"
                                                    className="size-5"
                                                />
                                            </span>
                                        </ListboxOption>
                                    );
                                })}
                            </div>
                        ))}
                    </ListboxOptions>
                </div>
            </Listbox>
            {/* oxlint-disable typescript/no-unnecessary-condition */}
            {!import.meta.env.SSR &&
                showLearnMore &&
                rect != null &&
                selectedOption != null &&
                createPortal(
                    <button
                        className="fixed z-50 cursor-pointer"
                        style={{
                            top: rect.top,
                            left: rect.left,
                            width: rect.width,
                            height: rect.height,
                        }}
                        role="button"
                        onClick={() => {
                            void analytics.track("PEPTIDES_LEARN_MORE_CLICKED", {
                                peptide_slug: selectedOption.key,
                                peptide_name: selectedOption.peptide.name,
                            });
                            void router.push("peptide_detail.index", {
                                slug: selectedOption.key,
                            });
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                    />,
                    document.body,
                )}
            {/* oxlint-enable typescript/no-unnecessary-condition */}
        </>
    );
}

// Tab icons
function CalculatorIcon(props: {className?: string}) {
    return (
        <svg
            className={props.className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
        >
            <rect x="4" y="2" width="16" height="20" rx="2" />
            <line x1="8" y1="6" x2="16" y2="6" />
            <line x1="8" y1="10" x2="10" y2="10" />
            <line x1="14" y1="10" x2="16" y2="10" />
            <line x1="8" y1="14" x2="10" y2="14" />
            <line x1="14" y1="14" x2="16" y2="14" />
            <line x1="8" y1="18" x2="10" y2="18" />
            <line x1="14" y1="18" x2="16" y2="18" />
        </svg>
    );
}

function VialsIcon(props: {className?: string}) {
    return (
        <svg
            className={props.className}
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
        >
            <path
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9.5 0.5h-5c-0.55228 0 -1 0.447715 -1 1V3h7V1.5c0 -0.552285 -0.4477 -1 -1 -1Z"
            />
            <path
                strokeLinejoin="round"
                strokeWidth={1}
                d="M2 5c0 -1.10457 0.89543 -2 2 -2h6c1.1046 0 2 0.89543 2 2v7.5c0 0.5523 -0.4477 1 -1 1H3c-0.55228 0 -1 -0.4477 -1 -1V5Z"
            />
        </svg>
    );
}

const VALID_UNITS: Unit[] = ["mg", "mcg", "IU"];
const VALID_SYRINGES = [0.3, 0.5, 1];

function readStateFromUrl(query: URLSearchParams) {
    // 1. Peptide
    const peptideParam = query.get("peptide");
    const isBlendKey = peptideParam != null && peptideParam in peptideData.blends;
    const isSimpleKey = peptideParam != null && peptideParam in peptideData.peptides;
    const selectedPeptideKey: AnyPeptideKey | null =
        isBlendKey || isSimpleKey ? (peptideParam as AnyPeptideKey) : null;

    // 2. Vial + vial_unit
    const vialParam = query.get("vial");
    const vialUnitParam = query.get("vial_unit");
    const parsedVial = vialParam != null ? parseFloat(vialParam) : NaN;
    const amountPerVial = !isNaN(parsedVial) && parsedVial > 0 ? parsedVial : null;
    const vialUnit: Unit =
        vialUnitParam != null && VALID_UNITS.includes(vialUnitParam as Unit)
            ? (vialUnitParam as Unit)
            : "mg";

    // 3. Component params (for blend peptides)
    let componentAmounts: Record<string, number | null> = {};
    let compoundOtherActive = false;
    if (selectedPeptideKey != null && isBlendKey) {
        const blend = peptideData.blends[selectedPeptideKey as BlendKey];
        const parsed: Record<string, number | null> = {};
        let hasAny = false;
        for (const component of blend.components) {
            const valStr = query.get(`compound_${component.peptide}`);
            if (valStr != null) {
                const val = parseFloat(valStr);
                parsed[component.peptide] = !isNaN(val) && val > 0 ? val : null;
                hasAny = true;
            }
        }
        if (hasAny) {
            componentAmounts = parsed;
            const matchedPreset = findMatchingPreset(blend.blendVials, parsed);
            compoundOtherActive = matchedPreset == null;
        }
    }

    // 4. Dose + dose_unit
    const doseParam = query.get("dose");
    const doseUnitParam = query.get("dose_unit");
    const parsedDose = doseParam != null ? parseFloat(doseParam) : NaN;
    const dose = !isNaN(parsedDose) && parsedDose > 0 ? parsedDose : null;
    const defaultDoseUnit: Unit = (() => {
        if (selectedPeptideKey == null) return "mcg";
        if (isBlendKey) {
            const blend = peptideData.blends[selectedPeptideKey as BlendKey];
            return getAnchorPeptide(blend).config.doses[0].unit;
        }
        const peptide = getSimplePeptideConfig(selectedPeptideKey);
        return "doses" in peptide ? (peptide as PeptideWithDoses).doses[0].unit : "mcg";
    })();
    const doseUnit: Unit =
        doseUnitParam != null && VALID_UNITS.includes(doseUnitParam as Unit)
            ? (doseUnitParam as Unit)
            : defaultDoseUnit;

    // 5. Syringe — null when absent (forces user to select)
    const syringeParam = query.get("syringe");
    const parsedSyringe = syringeParam != null ? parseFloat(syringeParam) : NaN;
    const syringeCapacity: number | null = VALID_SYRINGES.includes(parsedSyringe)
        ? parsedSyringe
        : selectedPeptideKey != null
          ? null
          : 1;

    // 6. Water — presence implies manual mode
    const waterParam = query.get("water");
    const parsedWater = waterParam != null ? parseFloat(waterParam) : NaN;
    const manualWaterInput = !isNaN(parsedWater) && parsedWater > 0 ? waterParam! : "";
    const resultMode: ResultMode = manualWaterInput !== "" ? "manual" : "auto";

    return {
        selectedPeptideKey,
        amountPerVial,
        vialUnit,
        dose,
        doseUnit,
        syringeCapacity,
        resultMode,
        manualWaterInput,
        compoundOtherActive,
        componentAmounts,
    };
}

function writeStateToUrl({
    selectedPeptideKey,
    amountPerVial,
    vialUnit,
    dose,
    doseUnit,
    syringeCapacity,
    resultMode,
    manualWaterInput,
    componentAmounts,
}: ReturnType<typeof readStateFromUrl>) {
    const params = new URLSearchParams();

    if (selectedPeptideKey != null) {
        params.set("peptide", selectedPeptideKey);

        const isBlend = selectedPeptideKey in peptideData.blends;

        if (isBlend) {
            // Blends always use compound_* params (preset or custom)
            for (const [key, v] of Object.entries(componentAmounts)) {
                if (v != null) {
                    params.set(`compound_${key}`, String(v));
                }
            }
        } else if (amountPerVial != null) {
            params.set("vial", amountPerVial.toString());
            params.set("vial_unit", vialUnit);
        }

        if (dose != null) {
            params.set("dose", dose.toString());
            params.set("dose_unit", doseUnit);
        }

        if (syringeCapacity != null) {
            params.set("syringe", syringeCapacity.toString());
        }

        if (resultMode === "manual" && manualWaterInput !== "") {
            params.set("water", manualWaterInput);
        }
    }

    return params.toString();
}

// Tab type
type PeptideTab = "calculator" | "vials";

export const Peptides = router.Peptides.useConditions((props) => {
    const peptidesData = props.resolved.peptides;
    const profile = useAppSelector(getProfile);
    const dispatch = useAppDispatch();
    const {request} = useSiteContext();
    const origin = new URL(request.url).origin;
    const [currentTab, setCurrentTab] = React.useState<PeptideTab>("calculator");

    // Read initial state from URL params
    const [initialState] = React.useState(() => readStateFromUrl(props.location.query));

    // Track URL restoration (only when a peptide was actually restored)
    React.useEffect(() => {
        if (initialState.selectedPeptideKey == null) return;
        const key = initialState.selectedPeptideKey;
        const isBlend = key in peptideData.blends;
        const name = isBlend
            ? peptideData.blends[key as BlendKey].name
            : getSimplePeptideConfig(key).name;
        void analytics.track("PEPTIDES_URL_RESTORED", {
            peptide_name: name,
            peptide_is_compound: isBlend,
        });
    }, []);

    // Calculator state
    const [selectedPeptideKey, setSelectedPeptideKey] =
        React.useState<AnyPeptideKey | null>(initialState.selectedPeptideKey);
    const [amountPerVial, setAmountPerVial] = React.useState<number | null>(
        initialState.amountPerVial,
    );
    const [waterMl, setWaterMl] = React.useState<number | null>(null);
    const [dose, setDose] = React.useState<number | null>(initialState.dose);
    const [syringeCapacity, setSyringeCapacity] = React.useState<number | null>(
        initialState.syringeCapacity,
    );
    const [vialUnit, setVialUnit] = React.useState<Unit>(initialState.vialUnit);
    const [doseUnit, setDoseUnit] = React.useState<Unit>(initialState.doseUnit);
    const [compoundOtherActive, setCompoundOtherActive] = React.useState(
        initialState.compoundOtherActive,
    );
    const [componentAmounts, setComponentAmounts] = React.useState<
        Record<string, number | null>
    >(initialState.componentAmounts);
    const [resultMode, setResultMode] = React.useState<ResultMode>(
        initialState.resultMode,
    );
    const [manualWaterInput, setManualWaterInput] = React.useState(
        initialState.manualWaterInput,
    );

    // Vials view state
    const [showLogDose, setShowLogDose] = React.useState<string | null>(null);
    const [doseUnits, setDoseUnits] = React.useState("");
    const [injectionSite, setInjectionSite] = React.useState("");
    const [isSaving, setIsSaving] = React.useState(false);

    // Modal state
    const [showHowWeCalculate, setShowHowWeCalculate] = React.useState(false);

    const queryString = writeStateToUrl({
        selectedPeptideKey,
        amountPerVial,
        vialUnit,
        dose,
        doseUnit,
        syringeCapacity,
        resultMode,
        manualWaterInput,
        componentAmounts,
        compoundOtherActive,
    });
    useQueryString(queryString);

    const selectedOption =
        selectedPeptideKey != null
            ? (peptideOptions.find((opt) => opt.key === selectedPeptideKey) ?? null)
            : null;
    const config =
        syringeCapacity != null
            ? (SYRINGE_CONFIG[syringeCapacity] ?? SYRINGE_CONFIG[1])
            : SYRINGE_CONFIG[1];

    // For blend peptides, derive config from the anchor (primary) component
    const anchor =
        selectedOption?.type === "blend"
            ? getAnchorPeptide(selectedOption.peptide)
            : null;

    // Get iuPerMg from selected peptide or anchor component
    const iuPerMg =
        anchor != null && "iuPerMg" in anchor.config
            ? (anchor.config as {iuPerMg: number}).iuPerMg
            : selectedOption?.type === "simple" && "iuPerMg" in selectedOption.peptide
              ? selectedOption.peptide.iuPerMg
              : undefined;

    // Convert values to mg for vial and mcg for dose calculations
    const isBlend = selectedOption?.type === "blend";
    const vialMgForCalc = (() => {
        if (isBlend) {
            // For blends, always sum componentAmounts (preset or custom)
            const values = Object.values(componentAmounts);
            if (values.length === 0 || values.some((v) => v == null || v <= 0)) {
                return null;
            }
            return values.reduce<number>((sum, v) => sum + v!, 0);
        }
        if (amountPerVial == null) return null;
        return vialUnit === "mcg"
            ? amountPerVial / 1000
            : vialUnit === "IU" && iuPerMg != null
              ? amountPerVial / iuPerMg
              : amountPerVial;
    })();
    // For compounds, anchorMg is the anchor component's mg in the vial
    const anchorMg = anchor != null ? (componentAmounts[anchor.key] ?? null) : null;

    const doseMcgForCalc = (() => {
        if (dose == null) return null;
        const baseMcg =
            doseUnit === "mg"
                ? dose * 1000
                : doseUnit === "IU" && iuPerMg != null
                  ? dose * (1000 / iuPerMg)
                  : dose;
        // For compounds, scale anchor dose to total compound dose
        if (
            anchor != null &&
            isBlend &&
            anchorMg != null &&
            anchorMg > 0 &&
            vialMgForCalc != null
        ) {
            return baseMcg * (vialMgForCalc / anchorMg);
        }
        return baseMcg;
    })();

    // Get max concentration for the selected peptide
    const maxConcentration =
        selectedOption != null
            ? getMaxConcentration(
                  selectedOption.peptide,
                  isBlend ? componentAmounts : undefined,
              )
            : DEFAULT_MAX_CONCENTRATION;

    // Calculate smart mix for syringe fill
    const smartOptions = React.useMemo(() => {
        if (
            vialMgForCalc == null ||
            doseMcgForCalc == null ||
            vialMgForCalc <= 0 ||
            doseMcgForCalc <= 0 ||
            syringeCapacity == null
        ) {
            return [];
        }
        return calculateSmartMix(
            vialMgForCalc,
            doseMcgForCalc,
            config,
            maxConcentration,
        );
    }, [vialMgForCalc, doseMcgForCalc, config, maxConcentration, syringeCapacity]);

    // Calculate effective values based on mode - single source of truth
    const manualParsed =
        resultMode === "manual" ? parseWaterInput(manualWaterInput) : null;
    const effectiveWaterMl =
        resultMode === "auto"
            ? (smartOptions[0]?.water ?? null)
            : (manualParsed?.value ?? null);
    const effectiveUnits =
        effectiveWaterMl != null && vialMgForCalc != null && doseMcgForCalc != null
            ? calculateUnits(vialMgForCalc, doseMcgForCalc, effectiveWaterMl)
            : null;
    const isOverflow = effectiveUnits != null && effectiveUnits > config.totalUnits;
    const fillUnits = Math.min(effectiveUnits ?? 0, config.totalUnits);

    // Update waterMl when effective water changes
    React.useEffect(() => {
        setWaterMl(effectiveWaterMl);
    }, [effectiveWaterMl]);

    // Handler for mode change with analytics
    const handleResultModeChange = (mode: ResultMode) => {
        if (mode === "manual" && resultMode === "auto" && selectedOption != null) {
            void analytics.track("PEPTIDES_SWITCHED_TO_MANUAL", {
                peptide_name: selectedOption.peptide.name,
                peptide_had_auto_results: smartOptions.length > 0,
            });
        }
        setResultMode(mode);
    };

    const handlePeptideChange = (option: PeptideOption) => {
        void analytics.track("PEPTIDES_SELECTED_PEPTIDE", {
            peptide_name: option.peptide.name,
            peptide_category: option.category,
        });
        setSelectedPeptideKey(option.key);
        // Reset values when peptide changes since options differ
        setAmountPerVial(null);
        setDose(null);
        setWaterMl(null);
        // Reset units - vial is always mg, dose defaults to first preset's unit
        setVialUnit("mg");
        const dosePeptide =
            option.type === "blend"
                ? getAnchorPeptide(option.peptide).config
                : option.peptide;
        const firstDose =
            "doses" in dosePeptide ? (dosePeptide as PeptideWithDoses).doses[0] : null;
        setDoseUnit(firstDose != null ? firstDose.unit : "mcg");
        // Reset compound state
        setCompoundOtherActive(false);
        setComponentAmounts({});
        // Reset syringe to default and auto mode
        setSyringeCapacity(1);
        setResultMode("auto");
        setManualWaterInput("");
    };

    const handleVialChange = (value: number | null) => {
        if (value != null && selectedOption?.type === "simple") {
            const isQuickSelect = selectedOption.peptide.commonVials.includes(value);
            const valueInMg =
                vialUnit === "mcg"
                    ? value / 1000
                    : vialUnit === "IU" && iuPerMg != null
                      ? value / iuPerMg
                      : value;
            void analytics.track("PEPTIDES_SELECTED_VIAL_SIZE", {
                peptide_name: selectedOption.peptide.name,
                peptide_vial_mg: valueInMg,
                peptide_vial_unit: vialUnit as "mg" | "mcg",
                peptide_selection_method: isQuickSelect ? "quick" : "other",
            });
            setCompoundOtherActive(false);
            setComponentAmounts({});
        }
        setAmountPerVial(value);
    };

    const handleBlendVialChange = (index: number | null) => {
        if (selectedOption?.type !== "blend") return;

        if (index != null) {
            const preset = selectedOption.peptide.blendVials[index];
            setComponentAmounts({...preset.components});
            setCompoundOtherActive(false);
            setAmountPerVial(null);
            const totalMg = getBlendPresetTotal(preset);
            void analytics.track("PEPTIDES_SELECTED_VIAL_SIZE", {
                peptide_name: selectedOption.peptide.name,
                peptide_vial_mg: totalMg,
                peptide_vial_unit: "mg" as const,
                peptide_selection_method: "quick" as const,
            });
        } else {
            // "Other" clicked
            setCompoundOtherActive(true);
            setComponentAmounts({});
            setAmountPerVial(null);
        }
    };

    const handleVialUnitChange = (newUnit: Unit) => {
        setVialUnit(newUnit);
    };

    const handleComponentChange = (peptideKey: string, value: number | null) => {
        setComponentAmounts((prev) => ({...prev, [peptideKey]: value}));
    };

    const handleDoseChange = (value: number | null) => {
        if (value != null && selectedOption != null) {
            // For compounds, check against anchor component's doses
            const dosePresets: DosePreset[] =
                selectedOption.type === "blend"
                    ? getAnchorPeptide(selectedOption.peptide).config.doses
                    : "doses" in selectedOption.peptide
                      ? (selectedOption.peptide as PeptideWithDoses).doses
                      : [];
            const matchedPreset = dosePresets.find((d) => d.value === value);
            // When a preset is selected, set doseUnit to match the preset's unit
            if (matchedPreset != null) {
                setDoseUnit(matchedPreset.unit);
            }
            // Convert to mcg for analytics
            const effectiveUnit = matchedPreset != null ? matchedPreset.unit : doseUnit;
            const valueInMcg =
                effectiveUnit === "mg"
                    ? value * 1000
                    : effectiveUnit === "IU" && iuPerMg != null
                      ? value * (1000 / iuPerMg)
                      : value;
            void analytics.track("PEPTIDES_SELECTED_DOSE", {
                peptide_name: selectedOption.peptide.name,
                peptide_dose_mcg: valueInMcg,
                peptide_dose_unit: effectiveUnit,
                peptide_selection_method: matchedPreset != null ? "quick" : "other",
            });
        }
        setDose(value);
    };

    const handleDoseUnitChange = (newUnit: Unit) => {
        setDoseUnit(newUnit);
    };

    const handleSyringeChange = (capacity: number) => {
        void analytics.track("PEPTIDES_CHANGED_SYRINGE", {
            peptide_syringe_capacity: capacity,
        });
        setSyringeCapacity(capacity);
    };

    // Track when a calculation is complete
    React.useEffect(() => {
        if (
            smartOptions.length > 0 &&
            selectedOption != null &&
            vialMgForCalc != null &&
            doseMcgForCalc != null &&
            syringeCapacity != null
        ) {
            const best = smartOptions[0];
            void analytics.track("PEPTIDES_CALCULATION_COMPLETE", {
                peptide_name: selectedOption.peptide.name,
                peptide_vial_mg: vialMgForCalc,
                peptide_dose_mcg: doseMcgForCalc,
                peptide_syringe_capacity: syringeCapacity,
                peptide_recommended_water_ml: best.water,
                peptide_recommended_units: best.units,
            });
        }
    }, [smartOptions, selectedOption, vialMgForCalc, doseMcgForCalc, syringeCapacity]);

    const handleSaveVial = async () => {
        if (
            selectedPeptideKey == null ||
            vialMgForCalc == null ||
            waterMl == null ||
            selectedOption == null
        ) {
            return;
        }

        const componentAmountsForSave: Record<string, string> = {};
        if (selectedOption.type === "blend") {
            for (const [key, value] of Object.entries(componentAmounts)) {
                if (value != null) {
                    componentAmountsForSave[key] = value.toString();
                }
            }
        }

        const result = await server.journal.peptides.create_peptide_vial({
            peptide_key: selectedPeptideKey,
            label: selectedOption.peptide.name,
            vial_mg: vialMgForCalc.toString(),
            water_ml: waterMl.toString(),
            component_amounts: componentAmountsForSave,
            mixed_at: new Date().toISOString(),
        });

        if (result.type === "success") {
            props.notify("Vial saved!", {type: "success"});
            props.setResolved((prev) => ({
                peptides: {
                    ...prev.peptides,
                    vials: [result.data, ...prev.peptides.vials],
                },
            }));
            if (profile != null && !profile.user.has_peptides) {
                dispatch({
                    type: "SET_PROFILE",
                    profile: {
                        ...profile,
                        user: {...profile.user, has_peptides: true},
                    },
                });
            }
            void analytics.track("PEPTIDES_VIAL_SAVED", {
                peptide_name: selectedOption.peptide.name,
                peptide_vial_mg: vialMgForCalc,
                peptide_mode: resultMode,
                peptide_water_ml: waterMl,
            });
            setCurrentTab("vials");
        } else {
            props.notify("Failed to save vial", {type: "error"});
        }
    };

    const canSaveVial =
        selectedPeptideKey != null &&
        vialMgForCalc != null &&
        vialMgForCalc > 0 &&
        waterMl != null &&
        waterMl > 0;

    // Validate dose units is a positive number
    const parsedDoseUnits = parseFloat(doseUnits);
    const isValidDoseUnits =
        doseUnits !== "" && !isNaN(parsedDoseUnits) && parsedDoseUnits > 0;

    // Vials handlers
    const handleLogDose = async (vialUuid: string) => {
        if (!isValidDoseUnits) return;

        setIsSaving(true);
        try {
            const result = await server.journal.peptides.create_peptide_dose({
                vial_uuid: vialUuid,
                units: doseUnits,
                taken_at: new Date().toISOString(),
                injection_site: injectionSite,
            });

            if (result.type === "success") {
                props.notify("Dose logged!", {type: "success"});
                setShowLogDose(null);
                setDoseUnits("");
                setInjectionSite("");
                props.setResolved((prev) => ({
                    peptides: {
                        ...prev.peptides,
                        vials: prev.peptides.vials.map((vialItem) =>
                            vialItem.vial.uuid === vialUuid
                                ? {
                                      ...vialItem,
                                      vial: {
                                          ...vialItem.vial,
                                          doses: [result.data, ...vialItem.vial.doses],
                                      },
                                      total_units_used: (
                                          Number(vialItem.total_units_used) +
                                          Number(result.data.units)
                                      ).toString(),
                                  }
                                : vialItem,
                        ),
                    },
                }));
            } else {
                props.notify("Failed to log dose", {type: "error"});
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteVial = async (vialUuid: string) => {
        const result = await server.journal.peptides.delete_peptide_vial({
            vial_uuid: vialUuid,
        });

        if (result.type === "success") {
            props.notify("Vial deleted", {type: "success"});
            // If this was the last vial, switch back to calculator
            if (peptidesData.vials.length === 1) {
                setCurrentTab("calculator");
            }
            props.setResolved((prev) => ({
                peptides: {
                    ...prev.peptides,
                    vials: prev.peptides.vials.filter(
                        (vialItem) => vialItem.vial.uuid !== vialUuid,
                    ),
                },
            }));
        } else {
            props.notify("Failed to archive vial", {type: "error"});
        }
    };

    const blendVialValue = (() => {
        if (selectedOption?.type !== "blend") return null;
        const presets = selectedOption.peptide.blendVials as BlendVialPreset[];
        const matched = findMatchingPreset(presets, componentAmounts);
        if (matched == null) return null;
        return presets.indexOf(matched);
    })();

    const share = async () => {
        const url = new URL(
            reverse("peptides"),
            utils.getBaseURLWithOriginForCapacitor(),
        );
        url.search = queryString;
        try {
            const code = await copyTrackedCalculatorLink(url.href, "PEPTIDES");
            props.notify("Link copied!", {type: "success"});
            void analytics.track("PEPTIDES_SHARED_CALCULATION", {
                peptide_name: selectedOption?.peptide.name,
                peptide_vial_mg: vialMgForCalc ?? undefined,
                peptide_dose_mcg: doseMcgForCalc ?? undefined,
                peptide_syringe_capacity: syringeCapacity ?? undefined,
                peptide_mode: resultMode,
                peptide_has_result: smartOptions.length > 0,
                shared_tracking_code: code,
            });
        } catch {
            props.notify("Could not copy the link. Please try again.", {
                type: "error",
            });
        }
    };
    const calculatorView = (
        <div className="flex h-full flex-col gap-joy-step">
            <PeptideDropdown
                selectedOption={selectedOption}
                onChange={handlePeptideChange}
            />
            <ul className="sr-only">
                {peptideOptions.map((opt) => (
                    <li key={opt.key}>
                        <a href={reverse("peptide_guide", {slug: opt.key})}>
                            {opt.peptide.name} — {opt.peptide.description}
                        </a>
                    </li>
                ))}
            </ul>

            <QuickSelectCard
                label="Amount per vial"
                options={
                    selectedOption == null
                        ? []
                        : selectedOption.type === "blend"
                          ? selectedOption.peptide.blendVials.map((preset, i) => ({
                                value: i,
                                label: getBlendPresetLabel(preset),
                            }))
                          : selectedOption.peptide.commonVials.map((v) => ({
                                value: v,
                                label: `${v} mg`,
                            }))
                }
                value={
                    selectedOption?.type === "blend" ? blendVialValue : amountPerVial
                }
                onChange={(value) => {
                    if (selectedOption?.type === "blend") {
                        handleBlendVialChange(value);
                    } else {
                        handleVialChange(value);
                    }
                }}
                otherPlaceholder="Enter amount"
                disabled={selectedOption == null}
                unit={vialUnit}
                onUnitChange={handleVialUnitChange}
                iuPerMg={iuPerMg}
                mode="vial"
                hideOtherInput={selectedOption?.type === "blend"}
                forceOther={compoundOtherActive && selectedOption?.type === "blend"}
            />

            {compoundOtherActive && selectedOption?.type === "blend" && (
                <CompoundVialInputs
                    components={selectedOption.peptide.components}
                    componentAmounts={componentAmounts}
                    onComponentChange={handleComponentChange}
                />
            )}

            <QuickSelectCard
                label={
                    anchor != null ? `Dose (${anchor.config.name})` : "Intended dose"
                }
                options={
                    selectedOption == null
                        ? []
                        : selectedOption.type === "blend"
                          ? getDoseOptions(
                                getAnchorPeptide(selectedOption.peptide).config,
                            )
                          : getDoseOptions(selectedOption.peptide)
                }
                value={dose}
                onChange={handleDoseChange}
                otherPlaceholder="Enter dose"
                disabled={selectedOption == null}
                unit={doseUnit}
                onUnitChange={handleDoseUnitChange}
                iuPerMg={iuPerMg}
                mode="dose"
            >
                {anchor != null &&
                    selectedOption?.type === "blend" &&
                    doseMcgForCalc != null &&
                    anchorMg != null &&
                    anchorMg > 0 && (
                        <CompoundDoseBreakdown
                            components={selectedOption.peptide.components}
                            anchorDoseMcg={
                                dose != null
                                    ? doseUnit === "mg"
                                        ? dose * 1000
                                        : doseUnit === "IU" && iuPerMg != null
                                          ? dose * (1000 / iuPerMg)
                                          : dose
                                    : 0
                            }
                            anchorMg={anchorMg}
                            componentAmounts={componentAmounts}
                        />
                    )}
            </QuickSelectCard>

            <SyringeIllustration
                capacity={syringeCapacity}
                fillUnits={fillUnits}
                onCapacityChange={handleSyringeChange}
                disabled={selectedOption == null}
                overflow={isOverflow}
            />

            <SmartMixResult
                vialMg={vialMgForCalc}
                doseMcg={doseMcgForCalc}
                syringeCapacity={syringeCapacity}
                maxConcentration={maxConcentration}
                disabled={selectedOption == null}
                onSaveVial={canSaveVial ? handleSaveVial : undefined}
                onShare={share}
                resultMode={resultMode}
                onResultModeChange={handleResultModeChange}
                manualWaterInput={manualWaterInput}
                onManualWaterInputChange={setManualWaterInput}
                units={effectiveUnits}
                validationError={manualParsed?.error ?? null}
                onShowHowWeCalculate={() => {
                    void analytics.track("PEPTIDES_SHOWED_FORMULA", {});
                    setShowHowWeCalculate(true);
                }}
            />
        </div>
    );

    const vialsView = (
        <div className="space-y-3">
            {peptidesData.vials.length === 0 ? (
                <div className="flex h-64 flex-col items-center justify-center text-center">
                    <SimpleIcon
                        icon="custom-dumbbell"
                        className="mb-4 size-12 text-gray-300 dark:text-slate-600"
                    />
                    <h2 className="mb-2 text-lg font-medium text-gray-900 dark:text-slate-100">
                        No vials yet
                    </h2>
                    <p className="text-gray-500 dark:text-slate-400">
                        Use the Calculator tab to mix a vial and save it here.
                    </p>
                </div>
            ) : (
                peptidesData.vials.map((vialItem) => {
                    const vialKey = vialItem.vial.peptide_key;
                    const peptideName =
                        vialKey in peptideData.blends
                            ? peptideData.blends[vialKey as BlendKey].name
                            : getSimplePeptideConfig(vialKey).name;

                    return (
                        <div
                            key={vialItem.vial.uuid}
                            className="rounded-xl bg-white px-4 py-3 outline-1 -outline-offset-1 outline-gray-300 transition-colors duration-200 outline-solid dark:bg-slate-800 dark:outline-slate-600"
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="text-lg font-medium text-gray-900 dark:text-slate-100">
                                        {peptideName}
                                    </div>
                                    <div className="text-sm text-gray-700 dark:text-slate-300">
                                        {formatNumber(vialItem.vial.vial_mg)} mg •{" "}
                                        {formatNumber(vialItem.vial.water_ml)} ml water
                                        • Mixed{" "}
                                        {getDaysSinceMixed(vialItem.vial.mixed_at)}{" "}
                                        {utils.pluralize(
                                            getDaysSinceMixed(vialItem.vial.mixed_at),
                                            "day",
                                        )}{" "}
                                        ago
                                    </div>
                                </div>
                                <AsyncButton
                                    onClick={() => handleDeleteVial(vialItem.vial.uuid)}
                                    className="text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
                                >
                                    <SimpleIcon icon="remove" className="size-5" />
                                </AsyncButton>
                            </div>

                            {showLogDose === vialItem.vial.uuid ? (
                                <div className="mt-3 space-y-2 border-t border-gray-100 pt-3 dark:border-slate-700">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                                            Units
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={doseUnits}
                                            onChange={(e) =>
                                                setDoseUnits(e.target.value)
                                            }
                                            placeholder="e.g., 10"
                                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                                            Injection site (optional)
                                        </label>
                                        <select
                                            value={injectionSite}
                                            onChange={(e) =>
                                                setInjectionSite(e.target.value)
                                            }
                                            className="form-select mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                                        >
                                            <option value="">Select site...</option>
                                            <option value="abdomen_left">
                                                Abdomen (Left)
                                            </option>
                                            <option value="abdomen_right">
                                                Abdomen (Right)
                                            </option>
                                            <option value="thigh_left">
                                                Thigh (Left)
                                            </option>
                                            <option value="thigh_right">
                                                Thigh (Right)
                                            </option>
                                            <option value="arm_left">Arm (Left)</option>
                                            <option value="arm_right">
                                                Arm (Right)
                                            </option>
                                            <option value="glute_left">
                                                Glute (Left)
                                            </option>
                                            <option value="glute_right">
                                                Glute (Right)
                                            </option>
                                        </select>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setShowLogDose(null)}
                                            className="flex-1 rounded-lg bg-gray-100 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleLogDose(vialItem.vial.uuid)
                                            }
                                            disabled={!isValidDoseUnits || isSaving}
                                            className="flex-1 rounded-lg bg-indigo-600 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                                        >
                                            {isSaving ? "Saving..." : "Log Dose"}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setShowLogDose(vialItem.vial.uuid)}
                                    className="mt-3 w-full rounded-lg bg-indigo-600 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                                >
                                    Log Dose
                                </button>
                            )}

                            {vialItem.vial.doses.length > 0 && (
                                <div className="mt-3 border-t border-gray-100 pt-3 dark:border-slate-700">
                                    <div className="text-xs font-medium text-gray-400 uppercase dark:text-slate-500">
                                        Recent doses
                                    </div>
                                    <div className="mt-1 space-y-1">
                                        {vialItem.vial.doses
                                            .slice(0, 3)
                                            .map((doseEntry) => (
                                                <div
                                                    key={doseEntry.uuid}
                                                    className="flex justify-between text-sm"
                                                >
                                                    <span className="text-gray-600 dark:text-slate-400">
                                                        {formatNumber(doseEntry.units)}{" "}
                                                        units
                                                        {doseEntry.injection_site !=
                                                            null &&
                                                            ` • ${doseEntry.injection_site.replace("_", " ")}`}
                                                    </span>
                                                    <span className="text-gray-400 dark:text-slate-500">
                                                        {dates.localeDate(
                                                            doseEntry.taken_at,
                                                        )}
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );

    return {
        options: {
            title: "Peptide Calculator",
            scene: {kind: "SIMPLE", nav: profile != null ? "menu" : "none"},
            respectDarkMode: true,
            titleSection:
                profile != null ? (
                    <div className="flex w-full items-center justify-center gap-3 py-1.5">
                        <graphics.Logo className="h-5 w-auto shrink-0 dark:invert" />
                        <span className="text-gray-300 dark:text-slate-600">|</span>
                        <h1 className="truncate text-xl font-light text-gray-600 dark:text-slate-300">
                            Peptides
                        </h1>
                    </div>
                ) : (
                    <div className="flex w-full items-center justify-center gap-3">
                        <graphics.Logo className="h-5 w-auto shrink-0 dark:invert" />
                        <span className="text-gray-300 dark:text-slate-600">|</span>
                        <h1 className="text-xl font-light text-gray-600 dark:text-slate-300">
                            Peptide Calculator
                        </h1>
                    </div>
                ),
            toolbarYouAreInChargeOfTransparency: (
                <SimpleTabs
                    tabs={[
                        {
                            id: "calculator",
                            label: "Calculator",
                            Icon: CalculatorIcon,
                        },
                        {
                            id: "vials",
                            label: "My Vials",
                            Icon: VialsIcon,
                            isHidden: peptidesData.vials.length === 0,
                        },
                    ]}
                    selectedTab={currentTab}
                    onSelectTab={setCurrentTab}
                />
            ),
        },
        actions: [],
        view: (
            <>
                <link rel="canonical" href={`${origin}${reverse("peptides")}`} />
                <meta
                    name="description"
                    content="Peptide calculator: enter your vial and dose to get the ideal water amount, exact syringe units, and a visual guide. Blends supported."
                />
                <meta
                    property="og:title"
                    content="Peptide Calculator | Joy Health Tracker"
                />
                <meta
                    property="og:description"
                    content="Peptide calculator: enter your vial and dose to get the ideal water amount, exact syringe units, and a visual guide. Blends supported."
                />
                <meta
                    property="og:image"
                    content={`${origin}${reverse("peptide_open_graph", {slug: "calculator"})}`}
                />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`${origin}${reverse("peptides")}`} />
                {currentTab === "calculator" ? calculatorView : vialsView}
                <CalculationModal
                    open={showHowWeCalculate}
                    content={HOW_WE_CALCULATE_CONTENT}
                    onClose={() => setShowHowWeCalculate(false)}
                />
            </>
        ),
    };
});
